import { FunctionComponent, useEffect, useState } from 'react';
import {
  Card,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableContainer,
  Tabs,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TableHeadCustom, TableNoData } from 'components/table';
import { CustomerActivity } from '../../PointsRewardsType';
import CustomerHistoryTableRow from '../../Table/CustomerHistoryTableRow';
import useTabs from 'hooks/useTabs';
import useTable, { getComparator } from 'hooks/useTable';
import useResponsive from 'hooks/useResponsive';

type Props = {
  customerActivities: CustomerActivity[];
};

const HistoryListing: FunctionComponent<Props> = ({ customerActivities }) => {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const TABLE_HEAD = [
    { id: 'created_at', label: t('date'), align: 'left' },
    { id: 'points', label: t('points'), align: 'center' },
    { id: 'status', label: t('status'), align: 'center' },
    { id: 'type', label: t('type'), align: 'right' },
    { id: 'notes', label: t('notes'), align: 'center' },
  ];

  const { order, orderBy, onSort } = useTable({
    defaultOrderBy: 'date',
  });

  const ROWS_PER_PAGE = 10;
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [skip, setSkip] = useState(0);
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } =
    useTabs('all');

  const mapPages = (data: CustomerActivity[]) => {
    const rows: React.ReactNode[] = [];
    const totalRows = skip + ROWS_PER_PAGE;

    for (let i = skip; i < totalRows; i++) {
      if (data[i] === undefined) break;

      rows.push(<CustomerHistoryTableRow key={data[i]._id} row={data[i]} />);
    }

    return rows;
  };

  const dataFiltered = applySortFilter({
    customerActivities,
    comparator: getComparator(order, orderBy),
    filterStatus,
  });

  const totalPage = Math.ceil(dataFiltered.length / ROWS_PER_PAGE);

  useEffect(() => {
    if (customerActivities.length) {
      const STATUS_OPTIONS = ['all'];
      customerActivities.forEach(element => {
        if (!STATUS_OPTIONS.includes(element.status)) {
          STATUS_OPTIONS.push(element.status);
        }
      });
      setStatusOptions(STATUS_OPTIONS);
    }
  }, []);

  let contentLoad: React.ReactNode = {};
  contentLoad = dataFiltered.length ? (
    <TableBody>{mapPages(dataFiltered)}</TableBody>
  ) : (
    <TableBody>
      <TableNoData isNotFound={true} Message={t('history')} />
    </TableBody>
  );

  return (
    <Stack>
      <Tabs
        allowScrollButtonsMobile
        variant='scrollable'
        scrollButtons='auto'
        value={filterStatus}
        onChange={onChangeFilterStatus}
        sx={{
          bgcolor: 'background.neutral',
          borderRadius: '10px',
          ml: 1,
          px: 2,
        }}
      >
        {statusOptions.map(tab => (
          <Tab disableRipple key={tab} label={t(tab)} value={tab} />
        ))}
      </Tabs>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: smUp ? 800 : '' }} aria-label='simple table'>
          <TableHeadCustom
            order={order}
            orderBy={orderBy}
            headLabel={TABLE_HEAD}
            onSort={onSort}
            cellBackground='#FFFFFF'
          />
          {contentLoad}
        </Table>
      </TableContainer>
      <Card
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: '25px',
          paddingTop: '25px',
        }}
      >
        {customerActivities && totalPage > 1 ? (
          <Pagination
            color='primary'
            variant='outlined'
            count={totalPage}
            page={Math.ceil(skip / ROWS_PER_PAGE + 1)}
            onChange={(e, pageNo) => setSkip((pageNo - 1) * ROWS_PER_PAGE)}
          />
        ) : null}
      </Card>
    </Stack>
  );
};

export default HistoryListing;

function applySortFilter({
  customerActivities,
  comparator,
  filterStatus,
}: {
  customerActivities: CustomerActivity[];
  comparator: (a: any, b: any) => number;
  filterStatus: string;
}) {
  const stabilizedThis = customerActivities.map(
    (el, index) => [el, index] as const
  );

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;

    return a[1] - b[1];
  });

  customerActivities = stabilizedThis.map(el => el[0]);

  if (filterStatus !== 'all') {
    customerActivities = customerActivities.filter(
      (item: Record<string, any>) => item.status === filterStatus
    );
  }

  return customerActivities;
}
